// Prerequisites: what concepts should be understood before this one
export const PREREQUISITES: Record<string, string[]> = {
  'position-displacement': [],
  'velocity-speed': ['position-displacement'],
  'acceleration': ['velocity-speed'],
  'kinematic-equations': ['acceleration'],
  'free-fall': ['kinematic-equations'],
  'projectile-motion': ['kinematic-equations'],
  'motion-graphs': ['velocity-speed', 'acceleration'],
  'newtons-first-law': ['velocity-speed'],
  'newtons-second-law': ['acceleration', 'newtons-first-law'],
  'newtons-third-law': ['newtons-second-law'],
  'free-body-diagrams': ['newtons-second-law'],
  'friction': ['free-body-diagrams'],
  'tension-normal': ['free-body-diagrams'],
  'inclined-planes': ['friction', 'free-body-diagrams'],
  'uniform-circular-motion': ['acceleration', 'newtons-second-law'],
  'centripetal-force': ['uniform-circular-motion'],
  'gravitational-force': ['newtons-second-law'],
  'orbits': ['gravitational-force', 'centripetal-force'],
  'work': ['newtons-second-law'],
  'kinetic-energy': ['work'],
  'potential-energy': ['work'],
  'conservation-of-energy': ['kinetic-energy', 'potential-energy'],
  'power': ['work'],
  'work-energy-theorem': ['kinetic-energy', 'work'],
  'linear-momentum': ['newtons-second-law'],
  'impulse': ['linear-momentum'],
  'conservation-of-momentum': ['linear-momentum'],
  'collisions': ['conservation-of-momentum'],
  'oscillations': ['acceleration'],
  'springs': ['oscillations', 'newtons-second-law'],
  'pendulums': ['oscillations'],
  'energy-in-shm': ['springs', 'conservation-of-energy'],
  'angular-quantities': ['velocity-speed', 'acceleration'],
  'torque': ['angular-quantities'],
  'rotational-inertia': ['torque'],
  'rotational-dynamics': ['rotational-inertia', 'newtons-second-law'],
  'angular-momentum': ['rotational-dynamics'],
};

// Concept connections for the concept map visualization
// Each concept lists concepts it directly leads to (children/next concepts)
// Computed as the inverse of PREREQUISITES: if B has prerequisite A, then A leads to B
export const CONCEPT_CONNECTIONS: Record<string, string[]> = {
  'position-displacement': ['velocity-speed'],
  'velocity-speed': ['acceleration', 'motion-graphs', 'newtons-first-law', 'angular-quantities'],
  'acceleration': [
    'kinematic-equations',
    'motion-graphs',
    'newtons-second-law',
    'uniform-circular-motion',
    'oscillations',
    'angular-quantities',
  ],
  'kinematic-equations': ['free-fall', 'projectile-motion'],
  'free-fall': [],
  'projectile-motion': [],
  'motion-graphs': [],
  'newtons-first-law': ['newtons-second-law'],
  'newtons-second-law': [
    'newtons-third-law',
    'free-body-diagrams',
    'uniform-circular-motion',
    'gravitational-force',
    'work',
    'linear-momentum',
    'springs',
    'rotational-dynamics',
  ],
  'newtons-third-law': [],
  'free-body-diagrams': ['friction', 'tension-normal', 'inclined-planes'],
  'friction': ['inclined-planes'],
  'tension-normal': [],
  'inclined-planes': [],
  'uniform-circular-motion': ['centripetal-force'],
  'centripetal-force': ['orbits'],
  'gravitational-force': ['orbits'],
  'orbits': [],
  'work': ['kinetic-energy', 'potential-energy', 'power', 'work-energy-theorem'],
  'kinetic-energy': ['conservation-of-energy', 'work-energy-theorem'],
  'potential-energy': ['conservation-of-energy'],
  'conservation-of-energy': ['energy-in-shm'],
  'power': [],
  'work-energy-theorem': [],
  'linear-momentum': ['impulse', 'conservation-of-momentum'],
  'impulse': [],
  'conservation-of-momentum': ['collisions'],
  'collisions': [],
  'oscillations': ['springs', 'pendulums'],
  'springs': ['energy-in-shm'],
  'pendulums': [],
  'energy-in-shm': [],
  'angular-quantities': ['torque'],
  'torque': ['rotational-inertia'],
  'rotational-inertia': ['rotational-dynamics'],
  'rotational-dynamics': ['angular-momentum'],
  'angular-momentum': [],
};

// Human-readable short names for the concept map nodes
export const CONCEPT_SHORT_NAMES: Record<string, string> = {
  'position-displacement': 'Position',
  'velocity-speed': 'Velocity',
  'acceleration': 'Acceleration',
  'kinematic-equations': 'Kinematics Eqs',
  'free-fall': 'Free Fall',
  'projectile-motion': 'Projectile Motion',
  'motion-graphs': 'Motion Graphs',
  'newtons-first-law': "Newton's 1st",
  'newtons-second-law': "Newton's 2nd",
  'newtons-third-law': "Newton's 3rd",
  'free-body-diagrams': 'Free Body Diagrams',
  'friction': 'Friction',
  'tension-normal': 'Tension & Normal',
  'inclined-planes': 'Inclined Planes',
  'uniform-circular-motion': 'Circular Motion',
  'centripetal-force': 'Centripetal Force',
  'gravitational-force': 'Gravity',
  'orbits': 'Orbits',
  'work': 'Work',
  'kinetic-energy': 'Kinetic Energy',
  'potential-energy': 'Potential Energy',
  'conservation-of-energy': 'Energy Conservation',
  'power': 'Power',
  'work-energy-theorem': 'Work-Energy Thm',
  'linear-momentum': 'Momentum',
  'impulse': 'Impulse',
  'conservation-of-momentum': 'Momentum Conservation',
  'collisions': 'Collisions',
  'oscillations': 'Oscillations',
  'springs': 'Springs',
  'pendulums': 'Pendulums',
  'energy-in-shm': 'Energy in SHM',
  'angular-quantities': 'Angular Quantities',
  'torque': 'Torque',
  'rotational-inertia': 'Rotational Inertia',
  'rotational-dynamics': 'Rotational Dynamics',
  'angular-momentum': 'Angular Momentum',
};

// Helper to get human-readable prerequisite names
export function getPrerequisiteNames(conceptId: string): string[] {
  const prereqs = PREREQUISITES[conceptId] || [];
  return prereqs.map(id => CONCEPT_SHORT_NAMES[id] || id);
}
